// Presigned uploads through the server's media job queue (cloudloop-flask
// UPLOADS.md): reserve with POST /upload/presign, PUT the file straight to
// Spaces, POST /upload/jobs/<id>/complete, then poll the job until it is done.
//
// No Vue, no XHR here: the caller passes in `api` (JSON calls to our server),
// `putFile` (the PUT to Spaces, with progress) and `sleep`, so this runs under
// node --test. FallbackToLegacy means "use the streaming POST /upload instead":
// the queue is off (404), unavailable (503), or the network failed before the
// first byte of the PUT (also what a missing bucket CORS rule looks like).

export const TERMINAL = new Set(["done", "failed", "dead", "expired"]);

export class FallbackToLegacy extends Error {
  constructor(reason) {
    super(`Presigned upload unavailable: ${reason}`);
    this.name = "FallbackToLegacy";
    this.reason = reason;
  }
}

export class UploadError extends Error {
  constructor(message, job = null) {
    super(message);
    this.name = "UploadError";
    this.job = job;
  }
}

// Headers a browser may not set itself (it sends Content-Length from the body).
const FORBIDDEN_HEADERS = new Set(["content-length", "host"]);

export function putHeaders(headers) {
  const out = {};
  for (const [k, v] of Object.entries(headers || {})) {
    if (!FORBIDDEN_HEADERS.has(k.toLowerCase())) out[k] = v;
  }
  return out;
}

export function messageFor(job) {
  if (!job) return "Upload failed";
  if (job.status === "failed") return job.error || "Upload failed";
  if (job.status === "dead") return "Processing failed; we've been alerted.";
  if (job.status === "expired")
    return "The upload took too long; please try again.";
  return "Upload failed";
}

// Poll interval: 1 s at first, then up to 5 s.
export function pollDelay(attempt) {
  return Math.min(5000, 1000 * Math.max(1, attempt));
}

async function presign(api, body) {
  let res;
  try {
    res = await api.presign(body);
  } catch (e) {
    throw new FallbackToLegacy("network error at presign");
  }
  if (res.status === 404 || res.status === 503) {
    throw new FallbackToLegacy(`presign answered ${res.status}`);
  }
  if (res.status === 409) {
    const job = res.json && res.json.job;
    // Someone (another tab, a retry) already has this loop in flight. Follow
    // it unless it is still waiting for a PUT we can't resume.
    if (job && job.job_id && job.status !== "awaiting_upload")
      return { follow: job };
    throw new UploadError("An upload for this loop is already in progress");
  }
  if (res.status < 200 || res.status >= 300) {
    throw new UploadError(
      (res.json && res.json.error) || `Upload refused (HTTP ${res.status})`,
    );
  }
  return { reservation: res.json };
}

async function put(putFile, reservation, file, onProgress) {
  let sawProgress = false;
  const progress = (pct) => {
    if (pct > 0) sawProgress = true;
    if (onProgress) onProgress(pct);
  };
  for (let attempt = 1; attempt <= 2; attempt++) {
    let res;
    try {
      res = await putFile(
        reservation.upload_url,
        putHeaders(reservation.headers),
        file,
        progress,
      );
    } catch (e) {
      // Nothing sent at all: blocked before the body started (no CORS rule,
      // Spaces unreachable). The streaming upload doesn't need Spaces from
      // the browser, so use it. Mid-body failures retry the PUT once.
      if (!sawProgress)
        throw new FallbackToLegacy("network error before the upload started");
      if (attempt === 2) throw new UploadError("Network error during upload");
      continue;
    }
    if (res.status >= 200 && res.status < 300) return;
    throw new UploadError(`Upload to storage failed (HTTP ${res.status})`);
  }
}

async function complete(api, jobId, sleep) {
  for (let attempt = 1; ; attempt++) {
    let res;
    try {
      res = await api.complete(jobId);
    } catch (e) {
      res = { status: 0 };
    }
    if (res.status === 202 || res.status === 200) return res.json;
    // 409: storage hasn't shown the object yet; 503 / network: retry briefly.
    if (
      (res.status === 409 || res.status === 503 || res.status === 0) &&
      attempt < 5
    ) {
      await sleep(1000 * attempt);
      continue;
    }
    throw new UploadError(
      (res.json && res.json.error) ||
        `Upload could not be queued (HTTP ${res.status})`,
    );
  }
}

async function follow(api, job, sleep, timeoutMs, now) {
  const deadline = now() + timeoutMs;
  for (let attempt = 1; !TERMINAL.has(job.status); attempt++) {
    if (now() > deadline) {
      throw new UploadError(
        "Still processing; it will appear in the session when it's done.",
        job,
      );
    }
    await sleep(pollDelay(attempt));
    try {
      const res = await api.job(job.job_id);
      if (res.status === 200 && res.json) job = res.json;
    } catch (e) {
      // transient: keep polling until the deadline
    }
  }
  return job;
}

// Returns the finished job (status "done", result.link). Throws
// FallbackToLegacy or UploadError (with .job when the server decided).
export async function uploadViaQueue({
  file,
  sessionName,
  loopHash,
  loopType = "Loop",
  api,
  putFile,
  onProgress = null,
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  now = () => Date.now(),
  timeoutMs = 20 * 60 * 1000,
  expiredRetries = 1,
}) {
  for (let round = 0; ; round++) {
    const { reservation, follow: inFlight } = await presign(api, {
      session_name: sessionName,
      loop_hash: loopHash,
      loop_type: loopType,
      size: file.size,
    });
    let job = inFlight;
    if (reservation) {
      await put(putFile, reservation, file, onProgress);
      job = await complete(api, reservation.job_id, sleep);
    }
    if (onProgress) onProgress(100);
    job = await follow(api, job, sleep, timeoutMs, now);
    if (job.status === "done") return job;
    if (job.status === "expired" && round < expiredRetries) continue;
    throw new UploadError(messageFor(job), job);
  }
}
